import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import './App.css';

// Components
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import ErrorBoundary from './components/ErrorBoundary';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import LevelQuiz from './pages/LevelQuiz';
import ResetPasswordPage from './pages/ResetPasswordPage';
import CustomerDashboard from './pages/CustomerDashboard';
import AdminDashboard from './pages/AdminDashboard';
import BookingCalendar from './pages/BookingCalendar';
import OpenMatches from './pages/OpenMatches';
import Trainings from './pages/Trainings';
import MemberOfTheMonth from './pages/MemberOfTheMonth';
import WorkPlan from './pages/WorkPlan';
import PublicMatch from './pages/PublicMatch';
import ProfilePage from './pages/ProfilePage';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import WaitlistConfirm from './pages/WaitlistConfirm';
import AdminCourts from './pages/AdminCourts';
import AdminBookings from './pages/AdminBookings';
import AdminUsers from './pages/AdminUsers';
import AdminVouchers from './pages/AdminVouchers';
import AdminPayments from './pages/AdminPayments';
import AdminRecurring from './pages/AdminRecurring';
import AdminCashRegister from './pages/AdminCashRegister';
import AdminAcademy from './pages/AdminAcademy';
import AdminTrainers from './pages/AdminTrainers';
import AdminTrainingPrograms from './pages/AdminTrainingPrograms';
import { ForcePasswordChange } from './components/ForcePasswordChange';
import { InsecureConnectionGuard, isInsecureConnection } from './components/InsecureConnectionGuard';
import { CardTerminalProvider } from './lib/cardTerminal';
import TrainerSchedule from './pages/TrainerSchedule';
import Community from './pages/Community';
import Leaderboard from './pages/Leaderboard';
import CustomerDetail from './pages/CustomerDetail';
import CourtTab from './pages/CourtTab';
import ActivateAccount from './pages/ActivateAccount';
import AdminMigration from './pages/AdminMigration';
import AdminAudit from './pages/AdminAudit';
import Terms from './pages/legal/Terms';
import Privacy from './pages/legal/Privacy';
import Imprint from './pages/legal/Imprint';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

// Token storage fallback (when cookies don't work behind proxies)
const TokenStore = {
  getAccess: () => localStorage.getItem('pa_access_token'),
  getRefresh: () => localStorage.getItem('pa_refresh_token'),
  set: (access, refresh) => {
    if (access) localStorage.setItem('pa_access_token', access);
    if (refresh) localStorage.setItem('pa_refresh_token', refresh);
  },
  clear: () => {
    localStorage.removeItem('pa_access_token');
    localStorage.removeItem('pa_refresh_token');
  }
};

// Request interceptor: attach token from localStorage if present
API.interceptors.request.use((config) => {
  const token = TokenStore.getAccess();
  if (token && !config.headers['Authorization']) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: auto-refresh on 401
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve();
  });
  failedQueue = [];
};

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/register') &&
      !originalRequest.url?.includes('/auth/refresh')
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => API(originalRequest));
      }
      originalRequest._retry = true;
      isRefreshing = true;
      try {
        const refreshToken = TokenStore.getRefresh();
        const headers = refreshToken ? { 'X-Refresh-Token': refreshToken } : {};
        const { data } = await axios.post(`${BACKEND_URL}/api/auth/refresh`, {}, { withCredentials: true, headers });
        if (data.access_token) {
          TokenStore.set(data.access_token, null);
        }
        processQueue(null);
        return API(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        TokenStore.clear();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// Translations
export const translations = {
  en: {
    // Navigation
    home: 'Home',
    book: 'Book Court',
    matches: 'Open Matches',
    profile: 'Profile',
    dashboard: 'Dashboard',
    admin: 'Admin',
    login: 'Login',
    register: 'Register',
    logout: 'Logout',
    
    // Common
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    cancel: 'Cancel',
    confirm: 'Confirm',
    save: 'Save',
    edit: 'Edit',
    delete: 'Delete',
    search: 'Search',
    filter: 'Filter',
    all: 'All',
    
    // Auth
    email: 'Email',
    password: 'Password',
    name: 'Name',
    phone: 'Phone',
    loginTitle: 'Welcome Back',
    registerTitle: 'Create Account',
    forgotPassword: 'Forgot Password?',
    noAccount: "Don't have an account?",
    hasAccount: 'Already have an account?',
    
    // Booking
    bookCourt: 'Book a Court',
    selectDate: 'Select Date',
    selectTime: 'Select Time',
    selectCourt: 'Select Court',
    duration: 'Duration',
    minutes: 'minutes',
    price: 'Price',
    payNow: 'Pay Now',
    useCredits: 'Use Credits',
    bookingConfirmed: 'Booking Confirmed!',
    noAvailableSlots: 'No available slots',
    
    // Matches
    openMatches: 'Open Matches',
    createMatch: 'Create Match',
    joinMatch: 'Join Match',
    leaveMatch: 'Leave Match',
    matchType: 'Match Type',
    players: 'Players',
    level: 'Level',
    levelRange: 'Level Range',
    spotsLeft: 'spots left',
    matchFull: 'Match Full',
    
    // Profile
    myProfile: 'My Profile',
    myBookings: 'My Bookings',
    myMatches: 'My Matches',
    credits: 'Credits & Passes',
    redeemVoucher: 'Redeem Voucher',
    voucherCode: 'Voucher Code',
    playerLevel: 'Player Level',
    
    // Admin
    adminDashboard: 'Admin Dashboard',
    courts: 'Courts',
    bookings: 'Bookings',
    users: 'Users',
    vouchers: 'Vouchers',
    recurring: 'Recurring',
    payments: 'Payments',
    revenue: 'Revenue',
    todayBookings: 'Today\'s Bookings',
    totalUsers: 'Total Users',
    
    // Levels
    beginner: 'Beginner',
    intermediate: 'Intermediate',
    advanced: 'Advanced',
  },
  de: {
    // Navigation
    home: 'Startseite',
    book: 'Platz buchen',
    matches: 'Offene Spiele',
    profile: 'Profil',
    dashboard: 'Übersicht',
    admin: 'Admin',
    login: 'Anmelden',
    register: 'Registrieren',
    logout: 'Abmelden',
    
    // Common
    loading: 'Laden...',
    error: 'Fehler',
    success: 'Erfolg',
    cancel: 'Abbrechen',
    confirm: 'Bestätigen',
    save: 'Speichern',
    edit: 'Bearbeiten',
    delete: 'Löschen',
    search: 'Suchen',
    filter: 'Filtern',
    all: 'Alle',
    
    // Auth
    email: 'E-Mail',
    password: 'Passwort',
    name: 'Name',
    phone: 'Telefon',
    loginTitle: 'Willkommen zurück',
    registerTitle: 'Konto erstellen',
    forgotPassword: 'Passwort vergessen?',
    noAccount: 'Noch kein Konto?',
    hasAccount: 'Bereits ein Konto?',
    
    // Booking
    bookCourt: 'Platz buchen',
    selectDate: 'Datum wählen',
    selectTime: 'Zeit wählen',
    selectCourt: 'Platz wählen',
    duration: 'Dauer',
    minutes: 'Minuten',
    price: 'Preis',
    payNow: 'Jetzt bezahlen',
    useCredits: 'Guthaben verwenden',
    bookingConfirmed: 'Buchung bestätigt!',
    noAvailableSlots: 'Keine freien Plätze',
    
    // Matches
    openMatches: 'Offene Spiele',
    createMatch: 'Spiel erstellen',
    joinMatch: 'Beitreten',
    leaveMatch: 'Verlassen',
    matchType: 'Spielart',
    players: 'Spieler',
    level: 'Level',
    levelRange: 'Level-Bereich',
    spotsLeft: 'Plätze frei',
    matchFull: 'Spiel voll',
    
    // Profile
    myProfile: 'Mein Profil',
    myBookings: 'Meine Buchungen',
    myMatches: 'Meine Spiele',
    credits: 'Guthaben & Abos',
    redeemVoucher: 'Gutschein einlösen',
    voucherCode: 'Gutscheincode',
    playerLevel: 'Spieler-Level',
    
    // Admin
    adminDashboard: 'Admin-Übersicht',
    courts: 'Plätze',
    bookings: 'Buchungen',
    users: 'Benutzer',
    vouchers: 'Gutscheine',
    recurring: 'Wiederkehrend',
    payments: 'Zahlungen',
    revenue: 'Umsatz',
    todayBookings: 'Heutige Buchungen',
    totalUsers: 'Benutzer gesamt',
    
    // Levels
    beginner: 'Anfänger',
    intermediate: 'Fortgeschritten',
    advanced: 'Profi',
  }
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState('en');

  const t = useCallback((key) => {
    return translations[language][key] || key;
  }, [language]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await API.get('/auth/me');
      setUser(data);
      setLanguage(data.preferred_language || 'en');
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await API.post('/auth/login', { email, password });
    TokenStore.set(data.access_token, data.refresh_token);
    setUser(data);
    setLanguage(data.preferred_language || 'en');
    return data;
  };

  const register = async (userData) => {
    const { data } = await API.post('/auth/register', userData);
    TokenStore.set(data.access_token, data.refresh_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    await API.post('/auth/logout');
    TokenStore.clear();
    setUser(false);
  };

  const refreshToken = async () => {
    try {
      await API.post('/auth/refresh');
    } catch {
      setUser(false);
    }
  };

  const updateLanguage = async (lang) => {
    setLanguage(lang);
    if (user) {
      await API.put('/users/me', { preferred_language: lang });
    }
  };

  return (
    <AuthContext.Provider value={useMemo(() => ({
      user,
      loading,
      login,
      register,
      logout,
      refreshToken,
      checkAuth,
      language,
      setLanguage: updateLanguage,
      t,
      API
    }), [user, loading, language, t, checkAuth])}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Only show the full-screen "Loading..." on the VERY first auth check
  // (user is still unresolved — `null`). Any background re-check must NOT
  // unmount the children, otherwise the scroll position jumps to the top
  // every time an auth refresh/re-check completes.
  if (loading && user === null) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-pulse text-emerald-600 text-xl font-bold tracking-wider">
          Loading...
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Level-quiz gate: every customer must set an initial level before using
  // the app. Verified members (blue tick) are exempt. The quiz page itself
  // is of course exempt.
  if (
    user.role === 'customer' &&
    !user.level_quiz_completed_at &&
    !user.is_member &&
    location.pathname !== '/onboarding/level-quiz'
  ) {
    return <Navigate to="/onboarding/level-quiz" replace />;
  }

  if (roles && !roles.includes(user.role)) {
    // Permission flag override: a trainer with `manages_academy` may access
    // /admin/academy (otherwise admin-only). /community is already public to
    // any authenticated user, so no override needed there.
    const allow =
      user.role === 'trainer' &&
      user.manages_academy &&
      location.pathname.startsWith('/admin/academy');
    if (!allow) {
      const fallback = user.role === 'trainer' ? '/trainer/schedule'
        : user.role === 'admin' ? '/admin'
        : user.role === 'staff' ? '/admin/bookings'
        : '/book';
      return <Navigate to={fallback} replace />;
    }
  }

  return children;
};

// Layout Component
const DashboardLayout = ({ children }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'staff';
  const isTrainer = user?.role === 'trainer';
  const isCustomer = user && !isAdmin && !isTrainer;
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  return (
    <div className="min-h-screen bg-[#F8FAFC] overflow-x-hidden">
      <Header onMenuClick={() => setSidebarOpen(true)} />
      <div className="flex">
        {(isAdmin || isTrainer) && <Sidebar mobileOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />}
        <main className={`flex-1 min-w-0 ${(isAdmin || isTrainer) ? 'lg:ml-64' : ''} pt-16 ${isCustomer ? 'pb-24 md:pb-0' : ''}`}>
          <div className="p-4 sm:p-6 md:p-8">
            <ErrorBoundary>{children}</ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
};

// Public Layout
const PublicLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <main className="pt-16">
        {children}
      </main>
    </div>
  );
};

// Routes the /book path to the right component depending on the user's role.
// Customers get the lightweight self-service calendar; trainers/admins/staff
// get the full daily grid with all customer names, statuses and live drag/drop.
const BookPageRouter = () => {
  const { user } = useAuth();
  const role = user?.role;
  if (role === 'admin' || role === 'staff' || role === 'trainer') {
    return <AdminBookings />;
  }
  return <BookingCalendar />;
};

function App() {
  if (isInsecureConnection()) return <InsecureConnectionGuard />;
  return (
    <BrowserRouter>
      <AuthProvider>
       <CardTerminalProvider>
        <ForcePasswordChange />
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: '#FFFFFF',
              color: '#0F172A',
              border: '1px solid #E2E8F0',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            },
          }}
        />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<PublicLayout><LoginPage /></PublicLayout>} />
          <Route path="/register" element={<PublicLayout><RegisterPage /></PublicLayout>} />
          <Route path="/forgot-password" element={<PublicLayout><ResetPasswordPage /></PublicLayout>} />
          <Route path="/reset-password" element={<PublicLayout><ResetPasswordPage /></PublicLayout>} />
          <Route path="/match/:matchId" element={<PublicMatch />} />
          <Route path="/waitlist/confirm" element={<PublicLayout><WaitlistConfirm /></PublicLayout>} />
          <Route path="/activate" element={<ActivateAccount />} />
          <Route path="/agb" element={<Terms />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/datenschutz" element={<Privacy />} />
          <Route path="/impressum" element={<Imprint />} />
          <Route path="/imprint" element={<Imprint />} />
          
          {/* Protected Customer Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardLayout><CustomerDashboard /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/book" element={
            <ProtectedRoute>
              <DashboardLayout><BookPageRouter /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/matches" element={
            <ProtectedRoute>
              <DashboardLayout><OpenMatches /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/trainings" element={
            <ProtectedRoute>
              <DashboardLayout><Trainings /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <DashboardLayout><ProfilePage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/onboarding/level-quiz" element={
            <ProtectedRoute>
              <DashboardLayout><LevelQuiz /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/payment/success" element={
            <ProtectedRoute>
              <DashboardLayout><PaymentSuccess /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/payment/cancel" element={
            <ProtectedRoute>
              <DashboardLayout><PaymentCancel /></DashboardLayout>
            </ProtectedRoute>
          } />

          {/* Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute roles={['admin']}>
              <DashboardLayout><AdminDashboard /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/courts" element={
            <ProtectedRoute roles={['admin']}>
              <DashboardLayout><AdminCourts /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/bookings" element={
            <ProtectedRoute roles={['admin', 'staff']}>
              <DashboardLayout><AdminBookings /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute roles={['admin', 'staff', 'trainer']}>
              <DashboardLayout><AdminUsers /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/vouchers" element={
            <ProtectedRoute roles={['admin', 'staff']}>
              <DashboardLayout><AdminVouchers /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/payments" element={
            <ProtectedRoute roles={['admin', 'staff']}>
              <DashboardLayout><AdminPayments /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/recurring" element={
            <ProtectedRoute roles={['admin']}>
              <DashboardLayout><AdminRecurring /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/cash-register" element={
            <ProtectedRoute roles={['admin', 'staff']}>
              <DashboardLayout><AdminCashRegister /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/trainers" element={
            <ProtectedRoute roles={['admin', 'staff']}>
              <DashboardLayout><AdminTrainers /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/training-programs" element={
            <ProtectedRoute roles={['admin', 'staff', 'trainer']}>
              <DashboardLayout><AdminTrainingPrograms /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/academy" element={
            <ProtectedRoute roles={['admin']}>
              <DashboardLayout><AdminAcademy /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/migration" element={
            <ProtectedRoute roles={['admin']}>
              <DashboardLayout><AdminMigration /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/audit" element={
            <ProtectedRoute roles={['admin']}>
              <DashboardLayout><AdminAudit /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/member-of-the-month" element={
            <ProtectedRoute roles={['admin', 'staff']}>
              <DashboardLayout><MemberOfTheMonth /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/work-plan" element={
            <ProtectedRoute roles={['admin', 'staff']}>
              <DashboardLayout><WorkPlan /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/trainer/schedule" element={
            <ProtectedRoute roles={['admin', 'staff', 'trainer']}>
              <DashboardLayout><TrainerSchedule /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/community" element={
            <ProtectedRoute>
              <DashboardLayout><Community /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/leaderboard" element={
            <ProtectedRoute>
              <DashboardLayout><Leaderboard /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/users/:userId" element={
            <ProtectedRoute roles={['admin', 'staff', 'trainer']}>
              <DashboardLayout><CustomerDetail /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/tabs/:tabId" element={
            <ProtectedRoute roles={['admin', 'staff']}>
              <DashboardLayout><CourtTab /></DashboardLayout>
            </ProtectedRoute>
          } />

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
       </CardTerminalProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
